import { deleteCookie } from 'cookies-next'
import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import { device } from '@/common/constants/device'
import { HASH_KEY, TOKEN_KEY } from '@/common/constants/cookies'
import Icon from '@/components/common/Icon'
import Text from '@/components/common/Text'
import HeaderDropdown from '@/components/layout/Header/common/HeaderDropdown'
import Button from '@/components/ui/Button'
import { clear } from '@/store/userSlice'

import HeaderUserMenuItem from './HeaderUserMenuItem'
import { userApi } from '@/common/services/api'
import getConfig from 'next/config'
import userLanguage from '@/common/services/userLanguage'
import { userAmplitudeAnalytics } from '@/common/services/userAmplitudeAnalytics'
import { ANALYTIC_ACCOUNT } from '@/common/constants/analytics'
import { FEATURE_REQUEST_MODAL } from '@/common/constants/modals'
import { open } from '@/store/modalsSlice'
import { useTranslations } from 'next-intl'

const BASE_URL = getConfig().publicRuntimeConfig.baseDomain

const User = styled.div`
  display: flex;
  align-items: center;

  @media ${device.laptopL} {
    padding: 4px 12px 4px 4px;

    border: 1px solid #d4dced;
    border-radius: 59px;

    max-width: 200px;
  }
`
const UserAvatar = styled.img`
  width: 36px;
  height: 36px;
`
const UserName = styled(Text)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`
const UserMenu = styled.div``
const DropdownIcon = styled(Icon)`
  transform: ${(props) => (props.active ? 'rotate(180deg)' : 'none')};
`
const UserLogoutButton = styled(Button)`
  @media ${device.toLaptopL} {
    display: none;
  }
`

const USER_MENU_LIST = [
  {
    icon: 'user',
    text: 'userListProfile',
    link: `/profile`,
    withAccess: true,
    needChangePath: false,
    analyticEvent: `${ANALYTIC_ACCOUNT}_profile_clicked`,
  },
  {
    icon: 'paper-plane',
    link: 'https://t.me/adheart_support_bot',
    text: 'navigationLinkSupportTelegram',
    needChangePath: false,
    analyticEvent: `${ANALYTIC_ACCOUNT}_support_tg_clicked`,
  },
  {
    icon: 'letter',
    link: 'mailto:support@adheart.me',
    text: 'navigationLinkSupport',
    needChangePath: false,
    analyticEvent: `${ANALYTIC_ACCOUNT}_support_clicked`,
  },
  {
    icon: 'rocket',
    link: `/premium`,
    text: 'userListPurchase',
    needChangePath: false,
    analyticEvent: `${ANALYTIC_ACCOUNT}_premium_clicked`,
  },
  {
    icon: 'support',
    link: '/contact',
    text: 'userListContacts',
    needChangePath: false,
  },
  {
    icon: 'service',
    text: 'requestFeature',
    analyticEvent: `${ANALYTIC_ACCOUNT}_feature_request_clicked`,
    type: 'button',
  },
]

const HeaderUser = ({ ...props }) => {
  const [isDropdownContentVisible, setIsDropdownContentVisible] =
    useState(false)
  const t = useTranslations('common')
  const homeUrl = `https://${BASE_URL}/${userLanguage.lang}`

  const dispatch = useDispatch()

  const { user, subscription } = useSelector((store) => store.user)

  const avatar = useMemo(
    () => user?.avatar || '/_next_static/icons/user.svg',
    [user]
  )

  const onTriggerClick = (val) => {
    userAmplitudeAnalytics.trackEvent(`${ANALYTIC_ACCOUNT}_clicked`)
    setIsDropdownContentVisible(val)
  }
  const onLogoutButtonClick = async () => {
    userAmplitudeAnalytics.trackEvent(`${ANALYTIC_ACCOUNT}_quit_clicked`)
    await userApi.logout()
    dispatch(clear())

    deleteCookie(TOKEN_KEY)
    deleteCookie(HASH_KEY, { domain: `.${BASE_URL}` })
    window.location.replace(homeUrl)
  }

  const onOpenModal = (item) => {
    if (item.icon === 'service') {
      dispatch(open(FEATURE_REQUEST_MODAL))
    }
  }

  const Trigger = (
    <User>
      <UserAvatar
        className="mr-2"
        src={avatar}
        alt={user?.name || user?.email}
      />
      <UserName className="mr-1" size="18">
        {user?.name || user?.email}
      </UserName>
      <DropdownIcon
        className="ml-auto"
        icon="dropdown-icon"
        color="black"
        active={isDropdownContentVisible}
      />
    </User>
  )

  return (
    <HeaderDropdown
      triggerClickCallback={onTriggerClick}
      isTriggerCallback={false}
      customTrigger={Trigger}
      contentMargin="30"
      {...props}
    >
      <UserMenu>
        {USER_MENU_LIST.map((item, index) => (
          <HeaderUserMenuItem
            key={index}
            onClick={() => {
              userAmplitudeAnalytics.trackEvent(item.analyticEvent)
              onOpenModal(item)
            }}
            subscriptionType={subscription.type}
            {...item}
          />
        ))}
      </UserMenu>
      <UserLogoutButton className="ml-auto mt-4" onClick={onLogoutButtonClick}>
        {t('quit')}
      </UserLogoutButton>
    </HeaderDropdown>
  )
}

export default HeaderUser
